import React, { useEffect, useRef, useState } from "react"
import "./Home.css"
import { HApi, HLink } from "src/core/helpers"
import { Document, Page, pdfjs } from "react-pdf"
import { HCF } from "@macashipo/mlib"
import { Button } from "reactstrap"
import { FiZoomIn, FiZoomOut } from "react-icons/fi"

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const PDFViewer = ({
  fileUrl,
  isUseIfFrame,
}: {
  fileUrl: string
  isUseIfFrame: any
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<number>(1200)
  const [numPages, setNumPages] = useState<number>(0)
  const [keyIframe, setKeyIframe] = useState<number>(Date.now())
  const [scale, setScale] = useState<number>(1)
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false)

  const isDriveFile = url => {
    if (url.startsWith("https://drive.google.com/")) {
      return true
    }
    return false
  }
  const customDriveFile4Preview = url => {
    let newSrc = ""
    newSrc = url
    if (url.indexOf("/view")) {
      newSrc = newSrc.replace("/view", "/preview")
    }
    if (url.indexOf("/edit")) {
      newSrc = newSrc.replace("/edit", "/preview")
    }
    return newSrc
  }
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setIsLoadSuccess(true)
  }

  const zoomIn = () => setScale(s => Math.min(s + 0.25, 3))
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.5))

  // 🖱️ Mouse dragging (grabbing) scroll
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    let isDragging = false
    let startX = 0
    let startY = 0
    let scrollLeft = 0
    let scrollTop = 0

    const mouseDownHandler = (e: MouseEvent) => {
      isDragging = true
      container.classList.add("grabbing")
      startX = e.pageX - container.offsetLeft
      startY = e.pageY - container.offsetTop
      scrollLeft = container.scrollLeft
      scrollTop = container.scrollTop
    }

    const mouseMoveHandler = (e: MouseEvent) => {
      if (!isDragging) return
      const x = e.pageX - container.offsetLeft
      const y = e.pageY - container.offsetTop
      const walkX = x - startX
      const walkY = y - startY
      container.scrollLeft = scrollLeft - walkX
      container.scrollTop = scrollTop - walkY
    }

    const mouseUpHandler = () => {
      isDragging = false
      container.classList.remove("grabbing")
    }

    container.addEventListener("mousedown", mouseDownHandler)
    container.addEventListener("mousemove", mouseMoveHandler)
    container.addEventListener("mouseup", mouseUpHandler)
    container.addEventListener("mouseleave", mouseUpHandler)

    return () => {
      container.removeEventListener("mousedown", mouseDownHandler)
      container.removeEventListener("mousemove", mouseMoveHandler)
      container.removeEventListener("mouseup", mouseUpHandler)
      container.removeEventListener("mouseleave", mouseUpHandler)
    }
  }, [scale])

  // 📏 Resize container
  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const { width } = entry.contentRect
        if (entry.target === document.body) {
          if (width < 1200) {
            if (width >= 992) {
              setContainerWidth(width - 40 - (width - 960))
            } else if (width >= 768) {
              setContainerWidth(width - 40 - (width - 720))
            } else if (width >= 576) {
              setContainerWidth(width - 40 - (width - 540))
            } else if (width > 0) {
              setContainerWidth(width)
            }
            setKeyIframe(Date.now())
          } else if (width >= 1200) {
            if (width >= 1400) {
              setContainerWidth(width - 40 - (width - 1320))
            } else {
              setContainerWidth(width - 40 - (width - 1140))
            }
            setKeyIframe(Date.now())
          }
        }
      }
    })
    resizeObserver.observe(document.body)
    return () => resizeObserver.unobserve(document.body)
  }, [])

  // 🖼️ Nếu dùng iframe
  if (isUseIfFrame) {
    return (
      <iframe
        key={keyIframe}
        src={fileUrl}
        width={containerWidth}
        height={1000}
        title="Xem file"
      />
    )
  }

  // 🧾 GGDrive PDF rendering
  if (isDriveFile(fileUrl)) {
    return (
      <div
        className="ggdrive-view"
        style={{
          width: containerWidth,
        }}
      >
        <iframe
          key={keyIframe}
          src={customDriveFile4Preview(fileUrl)}
          title="Xem file"
          allowFullScreen
        ></iframe>
      </div>
    )
  }

  // 🧾 React PDF rendering
  return (
    <div>
      <div className="container">
        {isLoadSuccess && (
          <div className="action">
            {scale !== 1 && <span className="px-2">{scale * 100}%</span>}
            <Button title="Phóng to" onClick={zoomIn}>
              <FiZoomIn />
            </Button>
            <Button title="Thu nhỏ" onClick={zoomOut}>
              <FiZoomOut />
            </Button>
          </div>
        )}
        <div
          ref={scrollRef}
          className={`${isLoadSuccess ? "grabbable" : ""}`}
          style={{
            width: "100%",
            height: "100%",
            overflow: "auto",
          }}
        >
          <div ref={containerRef}>
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading="Đang tải file..."
              error="Không mở được file! Vui lòng liên hệ hỗ trợ"
            >
              {Array.from(new Array(numPages), (_, index) => (
                <div key={`page_${index + 1}`} style={{ marginBottom: 20 }}>
                  <Page pageNumber={index + 1} width={containerWidth * scale} />
                </div>
              ))}
            </Document>
          </div>
        </div>
      </div>
    </div>
  )
}
const HomePage = () => {
  const [data, setData] = React.useState<any>(null)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string>("")
  const getGuidId = () => {
    const params: any = HLink.getAllParamOnUrl(window.location.href)
    return params?.guid_id
  }
  const requestData = () => {
    const guidId: any = getGuidId()
    setError("")
    setLoading(true)
    HApi.apiGeneric({
      method: "POST",
      url: `FumigationVerify/GetDataItem/${guidId}`,
      data: {},
    })
      .then(response => {
        setLoading(false)
        if (response.Data) {
          setData(response.Data)
        }
      })
      .catch(error => {
        setLoading(false)
        if (error?.msg) {
          setError(error.msg)
        }
      })
  }
  useEffect(() => {
    if (!data) {
      requestData()
    }
  }, [data])

  const renderLoading = () => {
    return (
      <div className="zz-fullcenter">
        <svg className="zz-spinner" viewBox="0 0 50 50">
          <circle
            className="path"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke-width="5"
          ></circle>
        </svg>
      </div>
    )
  }
  const renderMsg = msg => {
    if (msg && !loading) {
      return <div className="msg">{<p>{msg}</p>}</div>
    }
  }
  const renderFileView = () => {
    const useIfFrame = HCF.getCF("useIfFrame")
    return <PDFViewer isUseIfFrame={useIfFrame} fileUrl={data?.UrlFile} />
  }
  return (
    <div className="App">
      {data?.Header && (
        <div
          className="__header"
          dangerouslySetInnerHTML={{ __html: data.Header }}
        ></div>
      )}
      <div className="__main">
        {loading ? (
          renderLoading()
        ) : (
          <div className="container">
            {getGuidId() ? (
              <>
                {data?.UrlFile
                  ? renderFileView()
                  : renderMsg("Không có file nào! Vui lòng kiểm tra lại.")}
              </>
            ) : (
              <div className="w-100 h-100">
                <img
                  src="/css/imgs/default_bg_home.png"
                  alt="Ảnh trang chủ"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>
        )}
        {error && renderMsg(error)}
      </div>
      {data?.Footer && (
        <div
          className="__footer"
          dangerouslySetInnerHTML={{ __html: data.Footer }}
        ></div>
      )}
    </div>
  )
}

export default HomePage
